import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert
} from '@mui/material';
import { searchParameters } from '../Lookup_components/document_domainvalueslookup';
import debounce from 'lodash/debounce';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext';

// This is a simplified version of the StofAutocomplete from AnalysisTab.jsx
const ParameterAutocomplete = ({ value, onChange, ...props }) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const debouncedSearch = React.useMemo(
    () =>
      debounce((input) => {
        if (input.length < 2) {
          setOptions([]);
          return;
        }
        setLoading(true);
        searchParameters(input).then((results) => {
          setOptions(results || []);
          setLoading(false);
        });
      }, 300),
    [],
  );

  useEffect(() => {
    debouncedSearch(inputValue);
  }, [inputValue, debouncedSearch]);

  return (
    <Autocomplete
      {...props}
      value={value}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      loading={loading}
      getOptionLabel={(option) => option.description || ''}
      isOptionEqualToValue={(option, value) => option.domain_value_id === value.domain_value_id}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Zoek Parameter"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

function SuggestionDialog({ open, onClose, onSubmit, stofValue }) {
  const { user } = useContext(AuthContext);
  const [suggestedParameter, setSuggestedParameter] = useState(null);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!suggestedParameter) return;

    setIsSubmitting(true);
    setError(null);

    const submissionData = {
      original_stof_value: stofValue,
      suggested_parameter_id: suggestedParameter.domain_value_id,
      reason: reason,
      added_by_user_id: user.id,
    };

    const { error: insertError } = await supabase
      .from('stof_value_quality_suggestions')
      .insert(submissionData);

    setIsSubmitting(false);

    if (insertError) {
      console.error('Error submitting suggestion:', insertError);
      setError(insertError.message);
      // Don't close dialog on error
    } else {
      onSubmit(submissionData);
      // Reset form state after successful submission
      setSuggestedParameter(null);
      setReason('');
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Suggestie voor "{stofValue}"</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <ParameterAutocomplete
          value={suggestedParameter}
          onChange={setSuggestedParameter}
          sx={{ mt: 2 }}
        />
        <TextField
          label="Reden (optioneel)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          rows={3}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuleren</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!suggestedParameter || isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} /> : 'Indienen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SuggestionDialog; 